import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '../components/ui/Button';
import { captureMarketingAttribution, clearMarketingAttributionCookie } from '../utils/marketing-attribution';
import { trackMetaPageView } from '../utils/meta-pixel';
import {
  readTrackingConsentDecision,
  writeTrackingConsentDecision,
  type TrackingConsentDecision,
} from '../utils/tracking-consent';

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID?.trim() || null;

const MarketingAttributionProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const [decision, setDecision] = useState<TrackingConsentDecision | null>(() => readTrackingConsentDecision());
  const lastTrackedPathRef = useRef<string | null>(null);
  const currentPath = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (decision !== 'accepted') {
      return;
    }

    captureMarketingAttribution(location.search);
  }, [decision, location.search]);

  useEffect(() => {
    if (decision !== 'accepted' || !META_PIXEL_ID) {
      return;
    }

    if (lastTrackedPathRef.current === currentPath) {
      return;
    }

    trackMetaPageView(META_PIXEL_ID);
    lastTrackedPathRef.current = currentPath;
  }, [currentPath, decision]);

  const handleDecision = (nextDecision: TrackingConsentDecision) => {
    writeTrackingConsentDecision(nextDecision);
    setDecision(nextDecision);

    if (nextDecision === 'accepted') {
      captureMarketingAttribution(location.search);
      return;
    }

    clearMarketingAttributionCookie();
  };

  return (
    <>
      {children}
      {decision === null ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[70] flex justify-center px-4">
          <div className="pointer-events-auto w-full max-w-4xl rounded-2xl border border-border-strong bg-surface-elevated/95 p-4 shadow-2xl shadow-shadow-soft/40 backdrop-blur">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-text-primary">Cookies and marketing measurement</p>
                <p className="max-w-2xl text-sm text-text-muted-strong">
                  We use a first-party attribution cookie and Meta Pixel to understand which campaigns lead to signups and paid conversions. Declining keeps the app fully usable but reduces our marketing measurement.
                </p>
              </div>
              <div className="flex flex-wrap justify-end gap-3">
                <Button type="button" variant="secondary" onClick={() => handleDecision('declined')}>
                  Decline
                </Button>
                <Button type="button" onClick={() => handleDecision('accepted')}>
                  Accept
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default MarketingAttributionProvider;
